/**
 * FarmPacks promo bar.
 *
 * Horizon ships these messages as a slideshow that swaps one at a time. Here
 * they all sit still on one line: the offers on the left, the contact line
 * pushed to the right. Horizon renders every slide but the first as
 * aria-hidden, so this un-hides them and sorts them into the two sides. A
 * slide counts as contact when its link is a mailto: or tel: link.
 *
 * On a phone the contact line shrinks to an envelope icon; the link inside it
 * still carries the full address for screen readers. The offers there scroll
 * along by themselves (a CSS animation over a second copy of the offers, so
 * the loop has no gap). The first touch stops it for good and leaves the row
 * exactly where it was, to swipe by hand. Reduced motion: no scrolling.
 */
(() => {
  const ENVELOPE =
    '<svg viewBox="0 0 20 20" width="18" height="18" fill="none" aria-hidden="true" focusable="false">' +
    '<rect x="2.5" y="4.5" width="15" height="11" rx="1.5" stroke="currentColor" stroke-width="1.6"/>' +
    '<path d="M3 5.5l7 5.5 7-5.5" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/>' +
    '</svg>';

  const build = (root) => {
    if (root.dataset.fpSplit === '1') return;

    const track = root.querySelector('.announcement-bar__slides');
    if (!track) return;

    const slides = Array.from(track.children);
    if (!slides.length) return;

    root.dataset.fpSplit = '1';

    const start = document.createElement('div');
    start.className = 'announcement-bar__side announcement-bar__side--start';
    const end = document.createElement('div');
    end.className = 'announcement-bar__side announcement-bar__side--end';

    for (const slide of slides) {
      slide.removeAttribute('hidden');
      slide.setAttribute('aria-hidden', 'false');

      const isContact = slide.querySelector('a[href^="mailto:"], a[href^="tel:"]');
      if (isContact) {
        slide.classList.add('announcement-bar__slide--contact');
        slide.insertAdjacentHTML('afterbegin', '<span class="announcement-bar__icon">' + ENVELOPE + '</span>');
        end.appendChild(slide);
      } else {
        start.appendChild(slide);
      }
    }

    // Offers wrapped in one track: centred on a desktop, swipeable on a phone.
    const marquee = document.createElement('div');
    marquee.className = 'announcement-bar__marquee';
    while (start.firstChild) marquee.appendChild(start.firstChild);
    start.appendChild(marquee);

    root.removeAttribute('aria-live');
    root.classList.add('is-split');
    track.classList.add('is-split');
    track.appendChild(start);
    if (end.childElementCount) track.appendChild(end);
  };

  const SPEED = 32; // pixels per second
  const phone = window.matchMedia('(max-width: 749px)');
  const still = window.matchMedia('(prefers-reduced-motion: reduce)');

  const tick = (root) => {
    const side = root.querySelector('.announcement-bar__side--start');
    const marquee = side?.querySelector('.announcement-bar__marquee');
    if (!side || !marquee || side.dataset.fpTicker) return;
    if (!phone.matches || still.matches) return;
    const originals = Array.from(marquee.children);
    if (!originals.length) return;
    side.dataset.fpTicker = 'on';

    // The copies are for the eye only: hidden from screen readers, not
    // focusable or clickable, and without ids.
    const copies = originals.map((slide) => {
      const copy = slide.cloneNode(true);
      copy.classList.add('announcement-bar__slide--copy');
      copy.setAttribute('aria-hidden', 'true');
      copy.inert = true;
      copy.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
      marquee.appendChild(copy);
      return copy;
    });
    side.classList.add('is-ticking');
    marquee.style.setProperty('--fp-ticker-time', `${Math.max(8, marquee.scrollWidth / 2 / SPEED)}s`);

    const stop = () => {
      if (side.dataset.fpTicker !== 'on') return;
      side.dataset.fpTicker = 'off';
      // Where the animation had got to, within the first set of offers.
      const half = marquee.scrollWidth / 2;
      const matrix = new DOMMatrixReadOnly(getComputedStyle(marquee).transform);
      const offset = half ? (-matrix.m41 % half + half) % half : 0;
      side.classList.remove('is-ticking');
      // Drop the copies unless the row was stopped so near the end that the
      // offers alone could not scroll that far (it would jump back).
      if (offset <= half - side.clientWidth) copies.forEach((copy) => copy.remove());
      side.scrollLeft = offset;
    };
    side.addEventListener('touchstart', stop, { passive: true, once: true });
    side.addEventListener('pointerdown', stop, { once: true });
    side.addEventListener('focusin', stop, { once: true });
    phone.addEventListener('change', stop, { once: true });
    still.addEventListener('change', stop, { once: true });
  };

  const init = () => {
    document.querySelectorAll('announcement-bar-component').forEach((root) => {
      build(root);
      tick(root);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
